import { z } from 'zod';
import { contentIdSchema, pathSchema, traitKeySchema } from './content-id.js';
import { clampValueSchema } from './effect.js';
/**
 * Провенанс: предыстория каждой вычисленной величины
 * (D5, rules-engine.md, раздел 6).
 *
 * Возвращается вместе со значением всегда — это не отладочный режим
 * и не опция. Источник — идентификатор, а не готовый текст: подпись
 * собирается в интерфейсе через i18next (D11).
 */
export const provenanceSourceSchema = z.discriminatedUnion('kind', [
    /** Базовые правила системы: «КД начинается с 10». */
    z.strictObject({ kind: z.literal('ruleset'), key: pathSchema }),
    /** Характеристика персонажа: «+2 за Ловкость». */
    z.strictObject({ kind: z.literal('ability'), key: pathSchema }),
    /** Единица контента: предмет, заклинание, черта. */
    z.strictObject({ kind: z.literal('content'), id: contentIdSchema }),
    /** Эффект, заданный прямо на шаге журнала (D4). */
    z.strictObject({
        kind: z.literal('journal'),
        stepIndex: z.number().int().nonnegative(),
    }),
]);
/**
 * Шаг расчёта: что применили, с каким значением и что получилось
 * после него. Промежуточный `result` обязателен — без него по цепочке
 * не видно, где именно число стало не таким.
 */
/**
 * Величина, из которой взято значение операции (`ref`).
 *
 * Без неё две строки разбора кожаного доспеха выглядят повторами:
 * «Кожаный доспех 11» и «Кожаный доспех +0» — а вторая на самом деле
 * прибавка Ловкости, и ноль в ней объясняет, почему КД одиннадцать,
 * а не четырнадцать. Само число уже разрешено и лежит в `value`;
 * здесь — только откуда оно.
 */
const refPathSchema = pathSchema;
export const provenanceStepSchema = z.union([
    z.strictObject({
        op: z.enum(['set', 'add', 'mul']),
        /**
         * Значение операции уже после разрешения `ref`: в провенансе
         * стоит число, которое реально применили, а откуда оно взялось —
         * говорит `source` (rules-engine.md, раздел 6).
         */
        value: z.number(),
        result: z.number(),
        source: provenanceSourceSchema,
        ref: refPathSchema.optional(),
    }),
    z.strictObject({
        op: z.literal('add_capped'),
        /** Прибавленное после ограничения: столько реально попало в итог. */
        value: z.number(),
        /** Предел вклада — чтобы было видно, срезало его или нет (D21.1). */
        max: z.number(),
        result: z.number(),
        source: provenanceSourceSchema,
        ref: refPathSchema.optional(),
    }),
    z.strictObject({
        op: z.literal('clamp'),
        value: clampValueSchema,
        result: z.number(),
        source: provenanceSourceSchema,
    }),
    z.strictObject({
        op: z.literal('grant'),
        /** Добавленный признак (D21.3). */
        value: traitKeySchema,
        /** Набор целиком после шага: видно, что добавилось и к чему. */
        result: z.array(traitKeySchema),
        source: provenanceSourceSchema,
    }),
]);
export const provenanceSchema = z.strictObject({
    target: pathSchema,
    /**
     * Итог: число для числовой величины, набор признаков — для набора
     * (D21.3). Величина бывает либо тем, либо другим; смешение —
     * ошибка расчёта, а не приведение типов.
     */
    value: z.union([z.number(), z.array(traitKeySchema)]),
    /** Шаги в порядке применения. Отфильтрованных эффектов здесь нет. */
    steps: z.array(provenanceStepSchema),
});
//# sourceMappingURL=provenance.js.map